import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Plus, Calendar, Users, CheckSquare, ArrowRight, Eye } from 'lucide-react';
import PrintScheduleButton from '@/components/PrintScheduleButton';

export default function MeetingMinutesTab({
  projectId,
  meetingMinutes,
  selectedMinute,
  setSelectedMinute,
  project
}) {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button
        onClick={() => navigate('/')}
        variant="ghost"
        className="text-slate-600 hover:text-slate-900"
        data-testid="minutes-back-button"
      >
        ← Back to Dashboard
      </Button>
      
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-barlow font-semibold uppercase">Meeting Minutes</h2>
          <p className="text-sm text-slate-500 mt-1">{meetingMinutes.length} meeting minutes</p>
        </div>
        <div className="flex gap-2">
          <PrintScheduleButton moduleType="MeetingMinutes" data={meetingMinutes} project={project} />
          <Button 
            onClick={() => navigate(`/projects/${projectId}/meeting-minutes?action=new`)}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Minutes
          </Button>
        </div>
      </div>
      
      {meetingMinutes.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No meeting minutes recorded yet</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate(`/projects/${projectId}/meeting-minutes?action=new`)}
            >
              Record First Meeting
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {meetingMinutes.map(minute => (
            <Card 
              key={minute.id} 
              className={`cursor-pointer hover:border-slate-400 transition-colors ${selectedMinute?.id === minute.id ? 'border-slate-900 ring-1 ring-slate-900' : ''}`}
              onClick={() => setSelectedMinute(selectedMinute?.id === minute.id ? null : minute)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono font-bold">{minute.meeting_number || minute.id?.slice(0, 8)}</span>
                      <Badge className={minute.status === 'Final' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                        {minute.status || 'Draft'}
                      </Badge>
                    </div>
                    <p className="text-sm font-medium text-slate-700">{minute.title || minute.meeting_type}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span><Calendar className="h-3 w-3 inline mr-1" />{minute.meeting_date ? new Date(minute.meeting_date).toLocaleDateString() : '-'}</span>
                      <span><Users className="h-3 w-3 inline mr-1" />{minute.attendees?.length || 0} attendees</span>
                      <span><CheckSquare className="h-3 w-3 inline mr-1" />{minute.action_items?.length || 0} action items</span>
                    </div>
                  </div>
                  <ArrowRight className={`h-5 w-5 text-slate-400 transition-transform ${selectedMinute?.id === minute.id ? 'rotate-90' : ''}`} />
                </div>
                
                {/* Expanded Detail */}
                {selectedMinute?.id === minute.id && (
                  <div className="mt-4 pt-4 border-t space-y-3">
                    {minute.agenda && (
                      <div>
                        <p className="text-slate-500 text-sm font-medium">Agenda</p>
                        <p className="text-sm whitespace-pre-line">{minute.agenda}</p>
                      </div>
                    )}
                    {minute.attendees?.length > 0 && (
                      <div>
                        <p className="text-slate-500 text-sm font-medium">Attendees</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {minute.attendees.map((a, i) => (
                            <Badge key={i} variant="outline" className="text-xs">{a.name || a}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {minute.action_items?.length > 0 && (
                      <div>
                        <p className="text-slate-500 text-sm font-medium">Action Items ({minute.action_items.length})</p>
                        <ul className="list-disc list-inside text-sm mt-1">
                          {minute.action_items.slice(0, 3).map((item, i) => (
                            <li key={i} className="text-slate-700">{item.description || item.action}</li>
                          ))}
                          {minute.action_items.length > 3 && (
                            <li className="text-slate-500">...and {minute.action_items.length - 3} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                    <div className="flex gap-2 mt-4">
                      <Button size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/meeting-minutes/${minute.id}`); }}>
                        <Eye className="h-4 w-4 mr-1" />
                        View Full Minutes
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
